import React, { useEffect, useState, useCallback } from "react";
import { useLang } from "@/lib/i18n";
import { usePlan } from "@/context/PlanContext";
import api from "@/lib/api";
import { getReceiptBlob, saveBlob, shareBlob, receiptName } from "@/lib/receipt";
import Loader from "@/components/Loader";
import QrStatusCard from "@/components/QrStatusCard";
import { toast } from "sonner";
import { BadgeIndianRupee, Download, RefreshCw, Sparkles, CheckCircle2, XCircle, Clock, Share2 } from "lucide-react";

const BADGE = {
  active: { cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  expired: { cls: "bg-red-100 text-red-700", icon: XCircle },
  none: { cls: "bg-gray-100 text-gray-600", icon: Clock },
};

export default function MyPlan() {
  const { t } = useLang();
  const { payNow, price } = usePlan();
  const [data, setData] = useState(null);
  const [dl, setDl] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/subscription/me");
    setData(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader />;
  const sub = data.subscription;
  const statusKey = sub.status === "active" ? "plan_active" : sub.status === "expired" ? "plan_expired" : "plan_none";
  const B = BADGE[sub.status] || BADGE.none;
  const pct = sub.is_active ? Math.max(2, Math.min(100, Math.round((sub.days_left / 365) * 100))) : 0;
  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };

  const receipt = async (p) => {
    setDl(p.id);
    const { blob, error } = await getReceiptBlob(p.id);
    if (error) { toast.error(error); setDl(null); return; }
    const ok = saveBlob(blob, receiptName(p.payment_id || p.id));
    if (!ok) toast.message("Receipt opened in a new tab.");
    setDl(null);
  };

  const share = async (p) => {
    setDl(p.id);
    const { blob, error } = await getReceiptBlob(p.id);
    if (error) { toast.error(error); setDl(null); return; }
    const ok = await shareBlob(blob, receiptName(p.payment_id || p.id));
    if (!ok) { if (saveBlob(blob, receiptName(p.payment_id || p.id))) toast.message("Receipt downloaded."); }
    setDl(null);
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="myplan-page">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold flex items-center gap-2"><BadgeIndianRupee className="w-7 h-7 text-thali-green" /> {t("my_plan")}</h1>

      <QrStatusCard />

      <div className="card-thali space-y-4" data-testid="myplan-status-card">
        <div className="flex items-center justify-between">
          <div className="font-heading font-bold text-lg">{t("annual_plan")} · ₹{price}/{t("year")}</div>
          <span className={`text-sm px-3 py-1 rounded-full font-bold flex items-center gap-1 ${B.cls}`} data-testid="myplan-status-badge">
            <B.icon className="w-4 h-4" /> {t(statusKey)}
          </span>
        </div>

        {sub.is_active ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white border border-thali-turmeric/30 p-3"><div className="text-xs text-thali-ink/60">{t("start_date")}</div><div className="font-semibold">{fmt(sub.start)}</div></div>
              <div className="rounded-xl bg-white border border-thali-turmeric/30 p-3"><div className="text-xs text-thali-ink/60">{t("expiry_date")}</div><div className="font-semibold">{fmt(sub.expiry)}</div></div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1"><span className="text-thali-ink/60">{sub.days_left} {t("days_left")}</span><span className="text-thali-ink/40">365</span></div>
              <div className="h-2.5 rounded-full bg-thali-cream overflow-hidden"><div className="h-full bg-thali-green rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
            </div>
          </>
        ) : (
          <p className="text-sm text-thali-ink/70">
            {sub.status === "expired" ? "Your plan has expired. Renew to unlock saving and reports again. Your old entries are safe." : "Subscribe to save entries, generate reports and download PDF/Excel."}
          </p>
        )}

        <button onClick={payNow} className="btn-3d-green w-full" data-testid="myplan-renew-btn">
          {sub.is_active ? <><RefreshCw className="w-4 h-4" /> {t("renew")}</> : <><Sparkles className="w-4 h-4" /> {t("subscribe_now")} · ₹{price}</>}
        </button>
        {!sub.is_active && (
          <a href="/pay-qr" className="block text-center w-full text-sm font-bold text-thali-ink/70 py-1" data-testid="myplan-qr-btn">📷 {t("qr_or_scan")}</a>
        )}
      </div>

      <div className="card-thali" data-testid="myplan-history">
        <div className="font-heading font-bold text-lg mb-3">{t("payment_history")}</div>
        {data.payments.length === 0 ? (
          <p className="text-sm text-thali-ink/50">No payments yet.</p>
        ) : (
          <div className="space-y-2">
            {data.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-xl bg-white border border-thali-turmeric/20 p-3" data-testid={`myplan-pay-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">₹{p.amount} · <span className="capitalize text-thali-ink/70">{p.status}</span></div>
                  <div className="text-xs text-thali-ink/50 break-all">{p.payment_id || "—"} · {fmt(p.created_at)}</div>
                </div>
                {p.activated && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => receipt(p)} disabled={dl === p.id} className="btn-ghost text-xs px-2 py-1" data-testid={`myplan-receipt-${p.id}`}>
                      <Download className="w-3.5 h-3.5" /> {dl === p.id ? t("preparing_receipt") : t("download_receipt")}
                    </button>
                    <button onClick={() => share(p)} disabled={dl === p.id} className="btn-ghost text-xs px-2 py-1" data-testid={`myplan-share-${p.id}`} aria-label="Share receipt">
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
